import { SidebarNav } from "@/components/layout/Sidebar";
import { AthleteSidebar } from "@/components/layout/AthleteSidebar";
import { BottomNavBar } from "@/components/athlete/BottomNavBar";
import { CoachBottomNavBar } from "@/components/coach/BottomNavBar";
import { SidebarProvider } from "@/components/ui/sidebar";
import { useLocation } from "react-router-dom";
import { Header } from "@/components/layout/Header";
import { useScrollReset } from "@/hooks/useScrollReset";
import { useIsMobile } from "@/hooks/use-mobile";
import { useThemeCustomization } from "@/hooks/useThemeCustomization";

interface AppLayoutProps {
  children: React.ReactNode;
  isAdmin: boolean;
  isAthlete: boolean;
  isCoach?: boolean;
}

export function AppLayout({ children, isAdmin, isAthlete, isCoach = false }: AppLayoutProps) {
  const location = useLocation();
  const isMobile = useIsMobile();
  const { athleteThemeClass, athleteThemeStyle } = useThemeCustomization();
  const isHomePage = location.pathname === '/';
  const isLoginPage = location.pathname.includes('/auth');

  useScrollReset();

  const showSidebarAndHeader = !isHomePage && !isLoginPage;
  const showAdminSidebar = showSidebarAndHeader && (isAdmin || location.pathname.startsWith('/admin'));
  const showAthleteSidebar = showSidebarAndHeader && (isAthlete || location.pathname.startsWith('/athlete'));
  const showCoachSidebar = showSidebarAndHeader && (isCoach || location.pathname.startsWith('/coach'));

  // On mobile, athletes and coaches use bottom nav instead of sidebar
  const useAthleteBottomNav = isMobile && showAthleteSidebar;
  const useCoachBottomNav = isMobile && showCoachSidebar;
  const useBottomNav = useAthleteBottomNav || useCoachBottomNav;
  return (
    <SidebarProvider>
      <div
        className={`${showAdminSidebar ? 'admin-shell' : ''} ${showAthleteSidebar ? `athlete-shell ${athleteThemeClass}` : ''} ${showCoachSidebar ? `coach-shell ${athleteThemeClass.replace('athlete-shell', 'coach-shell')}` : ''} min-h-screen flex w-full`}
        style={showAthleteSidebar || showCoachSidebar ? athleteThemeStyle : undefined}
      >
        {showAdminSidebar && <SidebarNav />}

        {/* Coach: sidebar only on desktop, bottom nav on mobile */}
        {showCoachSidebar && !isMobile && <SidebarNav isCoach={true} />}

        {/* Athlete: show sidebar only on desktop */}
        {showAthleteSidebar && !isMobile && <AthleteSidebar />}

        <div className="flex-1 overflow-x-hidden">
          {showSidebarAndHeader && <Header hideMenuButton={useBottomNav} />}
          <main className={`pt-[calc(env(safe-area-inset-top)+3.5rem)] lg:pt-0 ${useBottomNav ? 'pb-[calc(env(safe-area-inset-bottom)+5rem)]' : 'pb-[calc(env(safe-area-inset-bottom)+1rem)]'}`}>
            {children}
          </main>
        </div>

        {!useBottomNav && <div className="safe-area-bottom-bg" />}

        {useAthleteBottomNav && <BottomNavBar />}
        {useCoachBottomNav && <CoachBottomNavBar />}
      </div>
    </SidebarProvider>
  );
}
